import { DateBox } from "devextreme-react/date-box";
import { SelectBox } from "devextreme-react/select-box";
import { TextArea } from "devextreme-react/text-area";
import { TextBox } from "devextreme-react/text-box";
import Button from "devextreme-react/button";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { useContext, useMemo, useRef, useState } from "react";
import { useGetLookupValuesByType } from "../../../../api/lookup";
import { Field } from "../../../../layouts";
import { AR, FileTypes, OrderStatus, PendingReason, ROType } from "../../../../types/enums";
import { RetailOrderContext } from "../context/RetailOrderContext";
import Validator, { RequiredRule } from "devextreme-react/validator";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { useSetRetailOrderToPending } from "../api/set-retail-order-to-pending";
import { LoadPopupPanel } from "../../../../components";

export default function SetToPendingButton() {

	const { order, refetchOrder } = useContext(RetailOrderContext);

	const [isPopupVisible, setPopupVisibility] = useState(false);
	const [pendingTypeID, setPendingTypeID] = useState(order.details.pendingTypeID);
	const [pendingReasonID, setPendingReasonID] = useState(order.details.pendingReasonID);
	const [customPendingReason, setCustomPendingReason] = useState(order.details.customPendingReason);
	const [pendingEndDate, setPendingEndDate] = useState(order.details.pendingEndDate);

	const pendingTypes = useGetLookupValuesByType('SyncPendingType', isPopupVisible);
	const pendingReasons = useGetLookupValuesByType('SyncPendingReason', isPopupVisible);
	const pendingReasonsData = useMemo(() => {
		return pendingReasons.data.filter(item => item.lookupID !== PendingReason.WaitingOnMaterial);
	}, [pendingReasons.data]);
	const commentRef = useRef<TextArea>(null);

	const setToPending = useSetRetailOrderToPending(
		() => {
			setPopupVisibility(false);
			showSuccessMessage(order.originalDetails.orderStatusID !== OrderStatus.Pending ?
				'Pending status has been set.' : 'Pending status has been updated.');
			refetchOrder();
		},
		(error) => showErrorMessage(error)
	)

	return (
		<>
			<Button
				elementAttr={{ 'data-testid': 'btn-set-ro-to-pending' }}
				text="Set to Pending"
				icon="Inscyth-Change-Orders"
				className="set-to-pending-button"
				visible={order.details.orderStatusID !== OrderStatus.Cancelled &&
					order.details.orderStatusID !== OrderStatus.Closed &&
					order.details.roTypeID !== ROType.Plumbing &&
					(order.details.activeARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate) ||
						order.details.activeARs.includes(AR.CfiToEnterTemplateDate) ||
						order.details.activeARs.includes(AR.CfiToEnterActualMeasureDate) ||
						order.details.activeARs.includes(AR.CfiToConfirmOrChangeOrder) ||
						order.details.activeARs.includes(AR.CfiToEnterScheduledInstallDate) ||
						order.details.activeARs.includes(AR.CfiToEnterActualInstallDate) ||
						order.details.activeARs.includes(AR.InstallerToVerifyCabinetQuality) ||
						order.details.activeARs.includes(AR.DealerToEnterCabinetPOReceivedDate))

				}
				disabled={isPopupVisible}
				onClick={() => {
					if (order.details.activeARs.includes(AR.InstallerToVerifyCabinetQuality) &&
						!order.details.files.find(x => x.fileTypeID === FileTypes.CabinetQualityCheckWorksheet)) {
						showErrorMessage("Cabinet Quality Check Worksheet is required, please upload before moving the order to pending");
					}
					else {
						setPopupVisibility(true);
					}
				}}
			>
			</Button>
			<Popup
				title="Put Job on Pending"
				width={600}
				height={530}
				visible={isPopupVisible}
				hideOnOutsideClick={true}
				onHiding={() => setPopupVisibility(false)}
			>
				<ToolbarItem
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: function () {
							setPopupVisibility(false);
						}
					}}
					widget="dxButton"
					location="after"
				/>
				<div className="field-label-25">
					<Field label="Pending Type">
						<SelectBox
							className="required"
							dataSource={pendingTypes.data}
							valueExpr={'lookupID'}
							displayExpr={'displayValue'}
							value={pendingTypeID}
							onValueChanged={(e) => setPendingTypeID(e.value)}
						>
							<Validator validationGroup='setToPendingGroup'>
								<RequiredRule />
							</Validator>
						</SelectBox>
					</Field>
					<Field label="Pending Reason">
						<SelectBox
							className="required"
							dataSource={pendingReasonsData}
							valueExpr={'lookupID'}
							displayExpr={'displayValue'}
							value={pendingReasonID}
							onValueChanged={(e) => {
								setPendingReasonID(e.value);
								if (e.value !== PendingReason.Other) {
									setCustomPendingReason(undefined);
								}
							}}
						>
							<Validator validationGroup='setToPendingGroup'>
								<RequiredRule />
							</Validator>
						</SelectBox>
					</Field>
					<Field label="Custom Reason">
						<TextBox
							className={pendingReasonID !== PendingReason.Other ? "" : "required"}
							disabled={pendingReasonID !== PendingReason.Other}
							value={customPendingReason}
							onValueChanged={(e) => setCustomPendingReason(e.value)}
						>
							<Validator validationGroup="setToPendingGroup">
								<RequiredRule message={'Custom Reason is required'} />
							</Validator>
						</TextBox>
					</Field>
					<Field label='Pending End Date'>
						<DateBox
							showClearButton={false}
							min={new Date()}
							value={pendingEndDate}
							onValueChanged={(e) => setPendingEndDate(e.value)}
						>
						</DateBox>
					</Field>
					<Field label={'Comment'}>
						<TextArea ref={commentRef} height={200} className="required">
							<Validator validationGroup="setToPendingGroup">
								<RequiredRule message={'Comment is required'} />
							</Validator>
						</TextArea>
					</Field>
				</div>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Submit',
						validationGroup: 'setToPendingGroup',
						onClick: (e: any) => {
							if (e.validationGroup.validate().isValid) {
								setToPending.mutate({
									retailOrderID: order.details.retailOrderID,
									pendingTypeID: pendingTypeID!,
									pendingReasonID: pendingReasonID!,
									customPendingReason: customPendingReason,
									pendingEndDate: pendingEndDate,
									comment: commentRef.current?.instance.option("value")!
								});
							}
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Cancel',
						onClick: () => setPopupVisibility(false)
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<LoadPopupPanel visible={pendingTypes.isLoading || pendingReasons.isLoading || setToPending.isLoading} />
			</Popup>
		</>
	)
}
